import React from 'react';
import { motion } from 'framer-motion';
import { UserCircle, Users, Edit, Trash2, X } from 'lucide-react';
import { safeFetch } from '../../../App';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Avatar } from '../../ui/Avatar';
import { Spinner } from '../../ui/Spinner';
import { EmptyState } from '../../ui/EmptyState';

/**
 * Grid of zone community members.
 *
 * The parent owns:
 *  - `members` list + loading state + a refetch hook
 *  - whether the current user already shared a profile (`isUserMember`)
 *
 * The modal delegates "open share profile" / "open member detail" /
 * "open edit profile" back to the parent via callbacks, since those
 * modals share the same selectedMember state.
 */
export function ZoneMembersModal({
    isOpen,
    onClose,
    chatId,
    zone,
    members,
    loading,
    isUserMember,
    currentUserMember,
    onShareProfile,
    onSelectMember,
    onEditProfile,
    refetchMembers,
}) {
    const handleMemberClick = async (member) => {
        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/get-member-details',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        encrypted_user_id: member.encrypted_user_id,
                        zone_id: zone,
                    }),
                },
            );
            if (!res.ok) {
                console.error('Failed to fetch member details');
                alert('שגיאה בטעינת פרטי חבר');
                return;
            }
            const fullMember = await res.json();
            onSelectMember?.(fullMember);
        } catch (err) {
            console.error('Error fetching member details:', err);
            alert('שגיאה בטעינת פרטי חבר');
        }
    };

    const handleRemoveProfile = async () => {
        if (!confirm('האם להסיר את הפרופיל שלך מהקהילה?')) return;
        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/remove-user-from-zone',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: chatId, zone_id: zone }),
                },
            );
            if (!res.ok) { alert('הסרת הפרופיל נכשלה'); return; }
            alert('הפרופיל הוסר מהאזור');
            await refetchMembers?.();
        } catch (err) {
            console.error('Remove profile error:', err);
            alert('שגיאה בהסרת הפרופיל');
        }
    };

    const sortedMembers = [...(members || [])].sort((a, b) => {
        if (a.own_profile) return -1;
        if (b.own_profile) return 1;
        return 0;
    });

    const isLoggedIn = chatId && chatId !== 'guest';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="חברי הקהילה"
            subtitle="הכירו את האנשים שמרכיבים את הקהילה שלנו"
            size="lg"
        >
            {isLoggedIn && !isUserMember && (
                <div className="flex justify-center mb-6">
                    <Button
                        variant="primary"
                        size="lg"
                        icon={<UserCircle />}
                        onClick={onShareProfile}
                    >
                        שתפו את הפרופיל שלכם בקהילה
                    </Button>
                </div>
            )}

            {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Spinner size="xl" />
                    <p className="text-ink-muted font-medium">טוען חברי קהילה...</p>
                </div>
            ) : sortedMembers.length === 0 ? (
                <EmptyState
                    icon={<Users />}
                    title="אין עדיין חברי קהילה"
                    description="היו הראשונים להצטרף לקהילה!"
                />
            ) : (
                <div className="flex flex-wrap justify-center gap-6">
                    {sortedMembers.map((member, idx) => (
                        <MemberTile
                            key={idx}
                            member={member}
                            index={idx}
                            isLoggedIn={isLoggedIn}
                            onClick={() => handleMemberClick(member)}
                            onEdit={() => onEditProfile?.(member || currentUserMember)}
                            onRemove={handleRemoveProfile}
                        />
                    ))}
                </div>
            )}
        </Modal>
    );
}

function MemberTile({ member, index, isLoggedIn, onClick, onEdit, onRemove }) {
    const isCurrentUser = member.own_profile === true;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25, delay: index * 0.04 }}
            className="flex flex-col items-center gap-2"
        >
            <div className="relative">
                <button
                    type="button"
                    onClick={onClick}
                    className="block w-20 h-20 rounded-pill overflow-hidden shadow-sm hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 ring-2 ring-brand-100"
                >
                    <Avatar
                        src={member.photo_url}
                        name={member.name}
                        size="xl"
                    />
                </button>

                {isCurrentUser && isLoggedIn && (
                    <div className="absolute -top-2 -end-2 flex flex-col gap-1">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            title="ערוך פרופיל"
                            className="w-8 h-8 rounded-pill bg-brand-500 hover:bg-brand-600 text-ink-inverse flex items-center justify-center shadow-sm transition-colors active:scale-95"
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            title="הסר מהקהילה"
                            className="w-8 h-8 rounded-pill bg-danger-500 hover:bg-danger-600 text-ink-inverse flex items-center justify-center shadow-sm transition-colors active:scale-95"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
            <p className="text-sm font-semibold text-ink text-center max-w-[80px] line-clamp-2">
                {member.name || 'חבר קהילה'}
            </p>
        </motion.div>
    );
}

export default ZoneMembersModal;
